/**
 * components/viewer/PipeLayer.jsx — Iter 51.
 *
 * Renders persistent pipe runs as SVG polylines over the sheet. Tap a
 * pipe → opens its inspector through the existing InspectorBridge.
 *
 * Add-Pipe mode coordination is owned by SheetViewer (tap structure A
 * then structure B). This layer is render + tap-to-open only.
 */
import React, { useEffect, useState } from "react";
import { listSheetPipes } from "@/lib/pipes";
import { useInspector } from "@/components/viewer/InspectorBridge";

export default function PipeLayer({ sheetId, imgDims, viewScale, reloadKey, addPipeMode }) {
  const [pipes, setPipes] = useState([]);
  const inspector = useInspector();
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;

  useEffect(() => {
    if (!sheetId) { setPipes([]); return; }
    let cancel = false;
    listSheetPipes(sheetId).then((p) => {
      if (!cancel) setPipes(p);
    });
    return () => { cancel = true; };
  }, [sheetId, reloadKey]);

  if (!pipes.length) return null;

  return (
    <svg
      className="absolute inset-0"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ pointerEvents: addPipeMode ? "none" : "auto" }}
      data-testid="pipe-layer"
      data-count={pipes.length}
    >
      {pipes.map((p) => {
        const pts = p.geometry || [];
        if (pts.length < 2) return null;
        const d = pts.map((pt, i) =>
          `${i === 0 ? "M" : "L"} ${pt.nx * W} ${pt.ny * H}`).join(" ");
        return (
          <g
            key={p.id}
            data-testid={`pipe-${p.id}`}
            data-pipe-number={p.pipe_number || ""}
            style={{ cursor: "pointer", pointerEvents: "stroke" }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              inspector.open({ kind: "pipe", id: p.id, payload: p });
            }}
          >
            {/* invisible-wide hit target so iPad tap is forgiving */}
            <path d={d} stroke="transparent" strokeWidth={14 / viewScale}
                   fill="none" vectorEffect="non-scaling-stroke" />
            {/* visible pipe line — calm storm-blue, dashed */}
            <path d={d}
                   stroke="rgba(59, 130, 246, 0.85)"
                   strokeWidth={2 / viewScale}
                   strokeDasharray={`${6 / viewScale} ${4 / viewScale}`}
                   fill="none"
                   strokeLinecap="round"
                   vectorEffect="non-scaling-stroke" />
            {/* arrowhead at the to-structure end */}
            {pts.length >= 2 && (() => {
              const a = pts[pts.length - 2];
              const b = pts[pts.length - 1];
              const dx = (b.nx - a.nx) * W;
              const dy = (b.ny - a.ny) * H;
              const len = Math.hypot(dx, dy) || 1;
              const ux = dx / len, uy = dy / len;
              const bx = b.nx * W, by = b.ny * H;
              const head = 8 / viewScale;
              const wing = 4 / viewScale;
              return (
                <path
                  d={`M ${bx - ux * head - uy * wing} ${by - uy * head + ux * wing} L ${bx} ${by} L ${bx - ux * head + uy * wing} ${by - uy * head - ux * wing}`}
                  stroke="rgba(59, 130, 246, 0.9)"
                  strokeWidth={1.2 / viewScale}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
              );
            })()}
            {/* pipe number label at midpoint */}
            {p.pipe_number && (() => {
              const mx = ((pts[0].nx + pts[pts.length - 1].nx) / 2) * W;
              const my = ((pts[0].ny + pts[pts.length - 1].ny) / 2) * H;
              const w = Math.max(40, (p.pipe_number.length + 2) * 6.5);
              return (
                <g transform={`translate(${mx}, ${my - 10 / viewScale})`}>
                  <rect x={-w / 2 / viewScale} y={-9 / viewScale}
                         width={w / viewScale} height={16 / viewScale}
                         rx={3 / viewScale}
                         fill="rgba(10,10,11,0.88)"
                         stroke="rgba(59, 130, 246, 0.7)"
                         strokeWidth={0.6 / viewScale} />
                  <text x={0} y={2 / viewScale} textAnchor="middle"
                         dominantBaseline="middle"
                         fill="rgba(59, 130, 246, 0.95)"
                         fontFamily="ui-monospace,Menlo,monospace"
                         fontSize={9 / viewScale}
                         fontWeight={700}>
                    {p.pipe_number}
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })}
    </svg>
  );
}
